// Color utility functions for dynamically calculating different color shades

type HexColor = string;
type RgbColor = [number, number, number];
type ColorLevels = number;

// Convert hex color to RGB array
function hexToRgb(hex: HexColor): RgbColor {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    throw new Error(`Invalid hex color: ${hex}`);
  }
  return [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)];
}

// Convert RGB array to hex color
function rgbToHex(r: number, g: number, b: number): HexColor {
  const toHex = (n: number) => {
    const hex = Math.round(Math.max(0, Math.min(255, n))).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Calculate lighter color shade
export function lightenColor(baseColor: HexColor, levels: ColorLevels = 3): HexColor {
  try {
    const [r, g, b] = hexToRgb(baseColor);

    // Calculate lighten factor (increase brightness by ~15% per level)
    const lightenFactor = 1 + levels * 0.15;

    const newR = Math.min(255, r + (255 - r) * (lightenFactor - 1));
    const newG = Math.min(255, g + (255 - g) * (lightenFactor - 1));
    const newB = Math.min(255, b + (255 - b) * (lightenFactor - 1));

    return rgbToHex(newR, newG, newB);
  } catch (error) {
    console.warn('Failed to lighten color:', error);
    return baseColor; // Return original color if failed
  }
}

// Calculate darker color shade
export function darkenColor(baseColor: HexColor, levels: ColorLevels = 3): HexColor {
  try {
    const [r, g, b] = hexToRgb(baseColor);

    // Calculate darken factor (decrease brightness by ~15% per level)
    const darkenFactor = Math.max(0, 1 - levels * 0.15);

    const newR = r * darkenFactor;
    const newG = g * darkenFactor;
    const newB = b * darkenFactor;

    return rgbToHex(newR, newG, newB);
  } catch (error) {
    console.warn('Failed to darken color:', error);
    return baseColor; // Return original color if failed
  }
}

// Generate Ant Design theme configuration color object
export function generateAntdThemeColors(primaryColor: HexColor) {
  return {
    colorPrimary: primaryColor,
    colorLink: primaryColor,
    colorLinkHover: lightenColor(primaryColor, 3), // Lighten by 3 levels
    colorLinkActive: lightenColor(primaryColor, 3) // Lighten by 3 levels
  };
}

// Apply global anchor (<a>) link colors for Umi Link elements and plain anchors
export function applyGlobalLinkColors(primaryColor: HexColor): void {
  const hoverColor = lightenColor(primaryColor, 3);

  const styleId = 'app-link-colors';
  let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }

  styleEl.textContent = `
    /* Umi Link and plain anchors */
    a, .umi-link:not([disabled]) { color: ${primaryColor}; }
    a:visited, .umi-link:visited:not([disabled]) { color: ${primaryColor}; }
    a:hover, .umi-link:hover:not([disabled]) { color: ${hoverColor}; }
    a:active, .umi-link:active:not([disabled]) { color: ${hoverColor}; }
    a:focus, .umi-link:focus:not([disabled]), a:focus-visible, .umi-link:focus-visible:not([disabled]) { color: ${hoverColor}; }
    /* Common Ant Typography anchors */
    .ant-typography a:not([disabled]) { color: ${primaryColor}; }
    .ant-typography a:hover:not([disabled]) { color: ${hoverColor}; }
    .ant-typography a:active:not([disabled]) { color: ${hoverColor}; }
  `;
}
